import { pasteContentExperience } from '@databases/frontend-metrics';
import { supportedImportMimeTypes } from '@databases/import-processor/types';
import { logWarning } from '@databases/logs';
import { type DefaultRootState } from '@databases/redux';
import {
  type Database,
  type DatabaseEntry,
  type DatabaseField,
  DatabaseFieldType,
  ImportedFileType
} from '@databases/types/database';
import { type DatabaseChangeType } from '@databases/types/redux';
import { type DatabaseChange } from '@databases/utils/database/changesets';
import {
  createEntry,
  createField,
  getDatabaseEntryOptional,
  getDatabaseFieldOptional,
  getExistingFieldNames
} from '@databases/utils/database/utils';
import { getUniqueFieldName } from '@databases/utils/field-name';
import { parseStorageFormat } from '@databases/utils/lib/dom';

import {
  ImportEntryMode,
  type ImportEntryReference,
  ImportFieldMode,
  type ImportFieldReference,
  type ImportResult,
  type ImportSettings,
  type ShowPasteFlag
} from '../types.ts';

import { convertStorageFormatToHtml } from './copy-helper.ts';

/**
 * An unreachable case that helps ensure the capture of uncovered cases
 * by explicitly making typescript error when used in conjunction
 * with a switch statement.
 */
export const unreachable = (value: never) => {
  throw new Error(`Not implemented: ${value}`);
};

/**
 * The supported paste MIME types.
 */
export const supportedPasteMimeTypes: Record<string, ImportedFileType> = {
  'text/html': ImportedFileType.HTML,
  ...supportedImportMimeTypes
};

/**
 * Determines the missing rows or columns based on the given all rows, selected rows and imported rows.
 * @param allItems - All rows or columns
 * @param selectedItems - Selected rows or columns
 * @param importedItems - Imported rows or columns
 * @returns the last 'n' entries of importedRows, where 'n' is the difference between the sum of the index and the length of importedRows,
 * and the length of allRows. Otherwise, returns an empty array.
 */
export const getMissingItems = <A>(
  allItems: string[],
  selectedItems: string[],
  importedItems: Array<A>
) => {
  const selectedIndex = allItems.indexOf(selectedItems[0]);

  const itemsToReturn = selectedIndex + importedItems.length - allItems.length;

  if (itemsToReturn > 0) {
    // Return the last items of the importedItems
    return importedItems.slice(-itemsToReturn);
  } else {
    return [];
  }
};

/**
 * Determines the import settings which contain entry references and field references with ImportEntryMode.EXISTING taking the
 * fieldIds and entryIds as input and starting from the selected indexes until the end of the fieldIds and entryIds.
 * @returns the import settings for the selected field and entry ids.
 */
export const getImportSettingsFromSelection = (
  selectedFieldIds: string[],
  selectedEntryIds: string[],
  fieldIds: string[],
  entryIds: string[]
): ImportSettings => {
  const index = selectedFieldIds.length === 0 ? 0 : fieldIds.indexOf(selectedFieldIds[0]);
  return {
    fieldReferences: fieldIds.slice(index).map((fieldId) => ({
      type: ImportFieldMode.EXISTING,
      fieldId
    })),
    entryReferences: entryIds
      .slice(entryIds.indexOf(selectedEntryIds[0]))
      .map((entryId) => ({
        type: ImportEntryMode.EXISTING,
        entryId
      })),
    selectedRows: selectedEntryIds.map(() => true),
    selectedColumns: selectedFieldIds.map(() => true)
  };
};

/**
 * Determines the import settings which contain entry references and field references with ImportEntryMode.CREATE and
 * ImportFieldMode.CREATE. Takes the settings for the selection and appends the missing fields and entries to the fieldReferences and
 * entryReferences. New fields are created with a unique name.
 * @returns the import settings for the selected field and entry ids as well as the missing fields and entries.
 */
export const getImportSettingsWithMissingItems = (
  selectedFieldIds: string[],
  selectedEntryIds: string[],
  fieldIds: string[],
  entryIds: string[],
  database: Database,
  importResult: ImportResult,
  fieldLabel: string
): ImportSettings => {
  const settings = getImportSettingsFromSelection(
    selectedFieldIds,
    selectedEntryIds,
    fieldIds,
    entryIds
  );
  const missingEntries = getMissingItems(entryIds, selectedEntryIds, importResult.rows);
  const missingFields = getMissingItems(
    fieldIds,
    selectedFieldIds,
    importResult.rows[0].cols
  );

  const addedFieldNames: string[] = [];
  return {
    ...settings,
    fieldReferences: [
      ...settings.fieldReferences,
      ...missingFields.map(() => {
        const uniqueFieldName = getUniqueFieldName(fieldLabel, [
          ...getExistingFieldNames(database),
          ...addedFieldNames
        ]);
        addedFieldNames.push(uniqueFieldName);
        return {
          type: ImportEntryMode.CREATE,
          field: {
            ...createField(DatabaseFieldType.TEXT),
            name: uniqueFieldName
          }
        };
      })
    ] as ImportFieldReference[],
    entryReferences: [
      ...settings.entryReferences,
      ...missingEntries.map(() => ({
        type: ImportFieldMode.CREATE,
        entry: createEntry(database)
      }))
    ] as ImportEntryReference[]
  };
};

// Soft selection helpers
export const getLastFieldIdByChangeType = (
  changeset: DatabaseChange[],
  changesetType: DatabaseChangeType
) => {
  return changeset
    .filter((change) => change.type === changesetType)
    .map((change) => {
      if ('field' in change && change.field) {
        return change.field.id;
      }

      if ('fieldId' in change) {
        return change.fieldId;
      }
    })
    .pop();
};

export const getLastEntryIdByChangeType = (
  changeset: DatabaseChange[],
  changesetType: DatabaseChangeType
) => {
  return changeset
    .filter((change) => change.type === changesetType)
    .map((change) => {
      if ('entry' in change) {
        return change.entry.id;
      }

      if ('entryId' in change) {
        return change.entryId;
      }
    })
    .pop();
};

const isStorageFormat = (value: string) => {
  const parsedStorageFormat = parseStorageFormat(value);
  const allElements = parsedStorageFormat.find('*');
  // All prefixes found on https://confluence.atlassian.com/doc/confluence-storage-format-790796544.html
  const prefixes = ['ac:', 'ri:', 'at:'];
  return Array.from(allElements).some((element) =>
    prefixes.some((prefix) => element.name.startsWith(prefix))
  );
};

export const convertToHtmlFormat = async (
  databaseName: string,
  state: DefaultRootState,
  items: {
    type: string;
    rawContent: string;
  }[]
) => {
  return await Promise.all(
    items.map(async (item) => {
      if (item.type === 'text/html' && isStorageFormat(item.rawContent)) {
        const htmlFormat = await convertStorageFormatToHtml(
          item.rawContent,
          databaseName,
          state,
          true
        );
        return {
          type: item.type,
          rawContent: htmlFormat
        };
      } else {
        return item;
      }
    })
  );
};

export function handleReadPasteError(
  pasteSupportedItems: {
    success: false;
    error:
      | { tag: 'UNSUPPORTED_MIME_TYPE'; fileType: string }
      | { tag: 'UNEXPECTED_DATA_TYPE' };
  },
  showFlag: ShowPasteFlag
) {
  pasteContentExperience.failure();
  switch (pasteSupportedItems.error.tag) {
    case 'UNSUPPORTED_MIME_TYPE':
      logWarning(
        new Error(`Unsupported mime type ${pasteSupportedItems.error.fileType}.`)
      );
      showFlag([{ actionType: 'cannotReadPastedContent', type: 'error' }]);
      return;
    case 'UNEXPECTED_DATA_TYPE':
      logWarning(new Error('Unexpected data type.'));
      showFlag([{ actionType: 'unexpectedDataType', type: 'error' }]);
      return;
    default:
      return unreachable(pasteSupportedItems.error);
  }
}

export function handleParsePasteError(showFlags: ShowPasteFlag) {
  pasteContentExperience.failure();
  logWarning(new Error('Pasted content cannot be read.'));
  showFlags([{ actionType: 'cannotReadPastedContent', type: 'error' }]);
  return;
}

export function handleApplyImportSettingsError(importResult: {
  success: false;
  error: { tag: 'UNKNOWN_FILE_TYPE'; fileType: string };
}) {
  pasteContentExperience.failure();
  switch (importResult.error.tag) {
    case 'UNKNOWN_FILE_TYPE':
      logWarning(new Error(`Unknown file type: ${importResult.error.fileType}.`));
      return;
    default:
      return unreachable(importResult.error.tag);
  }
}

export function getImageItems(items: DataTransferItemList) {
  return Array.from(items).filter((item) => item.type.startsWith('image/'));
}

type User = {
  accountId: string;
  _id: string;
};

export function isUserArray(value: unknown): value is User[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(
      (user) =>
        typeof user === 'object' &&
        'accountId' in user &&
        typeof user.accountId === 'string' &&
        '_id' in user &&
        typeof user._id === 'string'
    )
  );
}

export function resolveFieldReference(
  database: Database,
  fieldRef: ImportFieldReference | undefined
): DatabaseField | undefined {
  if (fieldRef?.type === ImportFieldMode.EXISTING) {
    return getDatabaseFieldOptional(database, fieldRef.fieldId);
  } else if (fieldRef?.type === ImportFieldMode.CREATE) {
    return fieldRef.field;
  }
}

export function resolveEntryReference(
  database: Database,
  entryRef: ImportEntryReference | undefined
): DatabaseEntry | undefined {
  if (entryRef?.type === ImportEntryMode.EXISTING) {
    return getDatabaseEntryOptional(database, entryRef.entryId);
  } else if (entryRef?.type === ImportEntryMode.CREATE) {
    return entryRef.entry;
  }
}

export const getNewSetValuesChangesets = (changesets: {
  preventChangesOnLock: DatabaseChange[];
  setValuesChangeset: DatabaseChange[];
}) => {
  const { preventChangesOnLock, setValuesChangeset } = changesets;

  const fieldIdsOfPreventedChanges = preventChangesOnLock
    .filter((change) => 'fieldId' in change || 'field' in change)
    .map((change) => ('fieldId' in change ? change.fieldId : change.field.id));

  // It is possible for the set_value changes to have multiple values. If the destination fields does not support
  // multi, we should not allow this. To prevent this from happening, we clear the set_value changes. However, it
  // is possible some of the changes are valid. Here we filter out the setValue changes that are prevented.
  return setValuesChangeset.filter(
    (change) =>
      !('fieldId' in change) || !fieldIdsOfPreventedChanges.includes(change.fieldId)
  );
};
